import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  getFirstCollision,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import type { Project, TaskCard as TaskCardType, TaskStatus } from '@witty-board/shared'
import { useMeQuery } from '../../api/auth.js'
import { useUsersQuery } from '../../api/users.js'
import {
  useCreateTaskMutation,
  useMoveTaskMutation,
  useTasksQuery,
  tasksQueryKey,
  type TaskListFilters,
} from '../../api/tasks.js'
import {
  TASK_STATUSES,
  applyOptimisticMove,
  groupTasksByStatus,
  projectScopedPosition,
} from '../../lib/board.js'
import { Column } from './Column.js'
import { TaskCard } from './TaskCard.js'
import { BoardSkeleton } from './BoardSkeleton.js'
import { DoneWindowSelect } from './DoneWindowSelect.js'
import { boardViewPath, taskPanelPath } from '../../lib/board-routes.js'

interface TaskBoardProps {
  filters: TaskListFilters
  projectsById: Map<string, Project>
  /** Clave bajo la que viven las rutas de tarea de este tablero (`:key` de `/projects/:key/tasks/:id`):
   *  la del proyecto real, o `MY_TASKS_KEY` en Mis tareas — nunca la del proyecto de la tarjeta pulsada,
   *  para no "salir" del tablero que se está mirando al abrir una tarea. */
  boardKey: string
  /** Sin esto, ninguna columna ofrece alta rápida: no hay un único proyecto al que asignar la tarea. */
  quickAddProjectId?: string
  /** "Mis tareas" (CLAUDE.md §6): la clave del proyecto se ve en cada tarjeta. */
  showProjectKey?: boolean
}

/**
 * Tablero de tres columnas, generalizado en la fase 5 para servir tanto al
 * tablero de un proyecto como a "Mis tareas" (varios proyectos a la vez): ver
 * docs/DECISIONS.md. La caché de TanStack Query es la única fuente de verdad
 * de la posición visual de cada tarjeta.
 */
export function TaskBoard({
  filters,
  projectsById,
  boardKey,
  quickAddProjectId,
  showProjectKey = false,
}: TaskBoardProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const tasksQuery = useTasksQuery(filters)
  const usersQuery = useUsersQuery()
  const me = useMeQuery()
  const moveTask = useMoveTaskMutation(filters)
  const createTask = useCreateTaskMutation(filters, me.data?.id ?? '')

  const [activeTaskId, setActiveTaskId] = useState<string | null>(null)
  /** Columna que es el destino actual del arrastre (CLAUDE.md §6): se resalta
   * como caja mientras dura el `over`, ver docs/DECISIONS.md. */
  const [dragOverStatus, setDragOverStatus] = useState<TaskStatus | null>(null)
  const [quickAddStatus, setQuickAddStatus] = useState<TaskStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  /** dnd-kit no suprime el `click` nativo tras soltar una tarjeta arrastrada;
   * en `ref` para no causar un render de más (ver docs/DECISIONS.md, fase 4). */
  const dragHappenedRef = useRef(false)
  /** Último `over.id` resuelto, para no perder el destino entre renders
   * cuando `collisionDetection` no encuentra colisión en un frame puntual
   * (ver docs/DECISIONS.md sobre el arrastre horizontal). */
  const lastOverIdRef = useRef<string | null>(null)

  const flatTasks = useMemo(() => tasksQuery.data ?? [], [tasksQuery.data])
  const columns = useMemo(() => groupTasksByStatus(flatTasks), [flatTasks])
  const usersById = useMemo(
    () => new Map((usersQuery.data ?? []).map((user) => [user.id, user] as const)),
    [usersQuery.data],
  )
  const activeTask = activeTaskId ? flatTasks.find((task) => task.id === activeTaskId) : undefined

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  /** Atajo global "c" (CLAUDE.md §6): solo tiene sentido con un proyecto único al que crear la tarea. */
  useEffect(() => {
    if (!quickAddProjectId) return
    function onShortcut(): void {
      setQuickAddStatus('todo')
    }
    window.addEventListener('wb:create-task', onShortcut)
    return () => window.removeEventListener('wb:create-task', onShortcut)
  }, [quickAddProjectId])

  function showError(message: string): void {
    setError(message)
    setTimeout(() => setError(null), 4000)
  }

  /**
   * `closestCorners` a secas compara esquinas contra tarjetas y columnas por
   * igual: al arrastrar en horizontal puro hacia una columna con pocas
   * tarjetas (o ninguna), sus esquinas pueden seguir más cerca de una
   * tarjeta de la columna de origen que de la columna destino, así que el
   * cambio de columna no se nota hasta que el cursor baja lo bastante para
   * acercarse a una tarjeta de la columna destino (bug real, encontrado en
   * producción — ver docs/DECISIONS.md). Estrategia en dos pasos, la misma
   * que usa el ejemplo oficial de dnd-kit para varios contenedores:
   * 1. `pointerWithin` — ¿el cursor está literalmente dentro de alguna
   *    columna? Esa comprobación no depende de dónde estén las tarjetas, así
   *    que entrar en una columna vacía (o con el cursor por encima de todas
   *    sus tarjetas) ya cuenta.
   * 2. Si el resultado es la columna en sí (no una tarjeta concreta), afina
   *    con `closestCenter` solo entre las tarjetas de esa columna, para que
   *    la tarjeta se inserte en el índice correcto y no siempre al final.
   */
  const collisionDetection: CollisionDetection = useCallback(
    (args) => {
      const pointerIntersections = pointerWithin(args)
      const intersections =
        pointerIntersections.length > 0 ? pointerIntersections : rectIntersection(args)
      let overId = getFirstCollision(intersections, 'id')

      if (overId != null) {
        if ((TASK_STATUSES as readonly string[]).includes(String(overId))) {
          const status = overId as TaskStatus
          const itemIds = new Set(columns[status].map((task) => task.id))
          if (itemIds.size > 0) {
            const refined = getFirstCollision(
              closestCenter({
                ...args,
                droppableContainers: args.droppableContainers.filter((container) =>
                  itemIds.has(String(container.id)),
                ),
              }),
              'id',
            )
            if (refined != null) overId = refined
          }
        }
        lastOverIdRef.current = String(overId)
        return [{ id: overId }]
      }

      return lastOverIdRef.current ? [{ id: lastOverIdRef.current }] : []
    },
    [columns],
  )

  function destinationFor(overId: string): { status: TaskStatus; index: number } | null {
    if ((TASK_STATUSES as readonly string[]).includes(overId)) {
      return { status: overId as TaskStatus, index: columns[overId as TaskStatus].length }
    }
    const status = TASK_STATUSES.find((candidate) =>
      columns[candidate].some((task) => task.id === overId),
    )
    if (!status) return null
    return { status, index: columns[status].findIndex((task) => task.id === overId) }
  }

  function onDragStart(event: DragStartEvent): void {
    dragHappenedRef.current = true
    lastOverIdRef.current = null
    setActiveTaskId(String(event.active.id))
  }

  function onDragCancel(): void {
    setActiveTaskId(null)
    setDragOverStatus(null)
  }

  function handleOpenTask(taskId: string): void {
    if (dragHappenedRef.current) {
      dragHappenedRef.current = false
      return
    }
    // `boardKey`, no la clave real del proyecto de la tarjeta: abrir una
    // tarea nunca debe cambiar qué tablero se ve debajo (CLAUDE.md §6, el
    // panel es un popup sobre el tablero actual, no una página aparte). Con
    // `search`, para que `?done=` no se pierda y el fondo no cambie de ventana.
    void navigate({ pathname: taskPanelPath(boardKey, taskId), search: location.search })
  }

  function onDragOver(event: DragOverEvent): void {
    const { active, over } = event
    if (!over) return
    const destination = destinationFor(String(over.id))
    if (!destination) return

    setDragOverStatus(destination.status)
    queryClient.setQueryData<TaskCardType[]>(tasksQueryKey(filters), (current) =>
      applyOptimisticMove(current ?? [], String(active.id), destination.status, destination.index),
    )
  }

  function onDragEnd(event: DragEndEvent): void {
    const { active, over } = event
    setActiveTaskId(null)
    setDragOverStatus(null)
    if (!over) return

    const destination = destinationFor(String(over.id))
    if (!destination) return

    const activeId = String(active.id)
    const position = projectScopedPosition(
      queryClient.getQueryData<TaskCardType[]>(tasksQueryKey(filters)) ?? flatTasks,
      activeId,
      destination.status,
    )

    moveTask.mutate(
      { id: activeId, status: destination.status, position },
      { onError: () => showError(t('board.errors.move')) },
    )
  }

  function handleCreate(status: TaskStatus, title: string): void {
    if (!quickAddProjectId) return
    createTask.mutate(
      { projectId: quickAddProjectId, title, status, priority: 'normal' },
      { onError: () => showError(t('board.errors.create')) },
    )
  }

  if (tasksQuery.isPending) return <BoardSkeleton />

  return (
    <div className="flex flex-col">
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragEnd={onDragEnd}
        onDragCancel={onDragCancel}
      >
        {/* Móvil (CLAUDE.md §6, fase 6): scroll horizontal con snap, una
            columna por pantalla; en escritorio son las tres a la vez. Sin
            altura fija: las columnas crecen y hace scroll la página. */}
        <div className="flex snap-x snap-mandatory gap-4 overflow-x-auto p-4 md:snap-none md:p-6">
          {TASK_STATUSES.map((status) => (
            <Column
              key={status}
              status={status}
              title={t(`board.columns.${status}`)}
              titleTo={{ pathname: boardViewPath(boardKey, status), search: location.search }}
              headerExtra={status === 'done' ? <DoneWindowSelect /> : undefined}
              tasks={columns[status]}
              projectsById={projectsById}
              usersById={usersById}
              showProjectKey={showProjectKey}
              isDropTarget={activeTaskId !== null && dragOverStatus === status}
              onCreateTask={quickAddProjectId ? (title) => handleCreate(status, title) : undefined}
              quickAddOpen={quickAddStatus === status}
              onRequestQuickAdd={() => setQuickAddStatus(status)}
              onCloseQuickAdd={() => setQuickAddStatus(null)}
              onOpenTask={handleOpenTask}
            />
          ))}
        </div>

        <DragOverlay>
          {activeTask && (
            <TaskCard
              task={activeTask}
              project={projectsById.get(activeTask.projectId)}
              assignee={activeTask.assigneeId ? usersById.get(activeTask.assigneeId) : undefined}
              showProjectKey={showProjectKey}
              onOpen={() => {}}
              isOverlay
            />
          )}
        </DragOverlay>
      </DndContext>

      {error && (
        <div
          role="alert"
          className="mx-6 mb-4 shrink-0 rounded-card bg-surface px-3 py-2 text-meta text-danger"
        >
          {error}
        </div>
      )}
    </div>
  )
}
